import styled, { css } from 'styled-components'
import { breakpoint } from '@sketch/global-styles'
import { RadioButton, Text } from '@sketch/components'

interface CardWrapperProps {
  isSelected: boolean
  isDisabled: boolean
}

export const OuterCardWrapper = styled.div`
  min-width: 208px;
  width: 100%;
  text-align: center;

  :not(:last-child) {
    margin-right: 24px;

    ${breakpoint('base', 'sm')`
        margin-bottom:  24px;
      `}
  }

  hr {
    width: 100%;
    margin: 19.2px 0; /* stylelint-disable-line scales/space */
  }

  ${breakpoint('base', 'sm')`
      width: 162px;
    `}
`

export const CardWrapper = styled.div<CardWrapperProps>(
  ({ theme, isSelected, isDisabled }) => css`
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    padding: 24px;
    height: 154px;
    width: 100%;
    border-radius: 20px; /* stylelint-disable-line scales/radii */
    position: relative;

    ${isSelected
      ? css`
          color: ${theme.colors.foreground.secondary.A};
          border: 2px solid ${theme.colors.sketch.A};
          background-color: ${theme.colors.background.secondary.A};
        `
      : css`
          color: ${theme.colors.foreground.secondary.D};
          border: 1px solid ${theme.colors.border.B};
          background-color: ${theme.colors.background.secondary.B};
        `}

    transition: background-color ${theme.transitions.duration[2]}
      ${theme.transitions.timing.easeInOut};

    ${isDisabled
      ? css`
          opacity: 0.8;
          cursor: not-allowed;

          input {
            cursor: not-allowed;
          }
        `
      : css`
          opacity: 1;
          &:hover {
            background-color: ${theme.colors.background.secondary.A};
          }
        `}

    &:active {
      background-color: ${theme.colors.background.tertiary.C};
    }
  `
)

export const FakeRadioButton = styled.div`
  position: absolute;
  top: 12px;
  left: 12px;
  width: 17px;
  height: 17px;
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 999px;
  background-color: white;
  box-shadow: inset 0 2px 0 0 rgba(0, 0, 0, 0.03);
  cursor: pointer;
  pointer-events: none;
  transition: ${({ theme }) => `
    background-color ${theme.transitions.duration[1]} ${theme.transitions.timing.easeInOut},
    border-color ${theme.transitions.duration[1]} ${theme.transitions.timing.easeInOut}
  `};

  &:after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    display: block;
    width: 6px;
    height: 6px;
    border-radius: 999px;
    background-color: white;
    margin: auto;
    opacity: 0;
    box-shadow: 0 1px 1px 0 ${({ theme }) => theme.colors.border.B};
  }
`

export const StyledRadioButton = styled(RadioButton)`
  position: absolute;
  top: 12px;
  right: 0;
  left: 12px;
  bottom: 0;

  ${RadioButton.Input} {
    ${({ disabled }) =>
      disabled &&
      css`
        opacity: 0.8 !important;
        cursor: not-allowed;
      `}
  }
`

export const PlanName = styled.p`
  width: 100%;
  text-align: center;
  font-size: ${({ theme }) => theme.fontSizes.E};
  font-weight: 600;
  margin-bottom: 4px;
`

export const Small = styled.small<{ isSelected: boolean }>`
  font-size: ${({ theme }) => theme.fontSizes.E};
  width: 100%;
  opacity: 0.65;
  pointer-events: none;
  color: ${({ theme, isSelected }) =>
    isSelected
      ? theme.colors.foreground.secondary.A
      : theme.colors.foreground.secondary.D};
  padding-top: 4px;
`

export const SmallText = styled.small`
  display: block;
  font-size: ${({ theme }) => theme.fontSizes.D};

  pointer-events: none;
  color: ${({ theme }) => theme.colors.foreground.secondary.D};
  line-height: ${({ theme }) => theme.lineHeights.E};
  margin-top: 16px;
`

export const Price = styled.div<{ isSelected: boolean }>(
  ({ theme, isSelected }) => css`
    display: flex;
    align-items: flex-start;
    justify-content: center;
    font-weight: ${theme.fontWeights.regular};
    color: ${isSelected
      ? theme.colors.foreground.secondary.A
      : theme.colors.foreground.secondary.D};
  `
)

export const Currency = styled.span(
  ({ theme }) => css`
    font-size: ${theme.fontSizes.I};
    margin-right: 8px;
    letter-spacing: -6px;
  `
)

export const Unit = styled.p`
  align-self: flex-start;
  margin: 0;
  line-height: 1;
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  font-size: 56px; /* stylelint-disable-line scales/font-size */
`

export const Cents = styled.p`
  margin: 0;
  font-size: 1.625rem; /* stylelint-disable-line scales/font-size */
  letter-spacing: 0;
`

export const MobileCard = styled.div<{
  isSelected: boolean
  isDisabled: boolean
}>(
  ({ theme, isSelected, isDisabled }) => css`
    position: relative;
    margin-bottom: 22px; /* stylelint-disable-line scales/space */
    width: 100%;
    border-radius: ${theme.radii.xlarge};

    ${isSelected
      ? css`
          color: ${theme.colors.foreground.secondary.A};
          border: 2px solid ${theme.colors.sketch.A};
          background-color: ${theme.colors.background.secondary.A};
        `
      : css`
          color: ${theme.colors.foreground.secondary.D};
          border: 1px solid ${theme.colors.border.B};
          background-color: ${theme.colors.background.secondary.B};
        `}

    opacity: ${isDisabled ? '0.8' : '1'};
    transition: background-color ${theme.transitions.duration[2]}
      ${theme.transitions.timing.easeInOut};

    &:hover {
      background-color: ${theme.colors.background.secondary.A};
    }

    &:not(:last-child) {
      margin-right: 12px;
    }

    &:active {
      background-color: ${theme.colors.background.tertiary.C};
    }
  `
)

export const MobileRadioButton = styled(RadioButton)`
  position: absolute;
  top: 35%;
  left: 12px;

  ${RadioButton.Input} {
    ${({ disabled }) =>
      disabled &&
      css`
        opacity: 0.8 !important;
        cursor: not-allowed;
      `}
  }
`

export const MobilePlanContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: 40px;
  padding: 12px 0;
`

export const MobilePlanName = styled(Text).attrs({
  textStyle: 'subheader.primary.bold.C',
})<{ isSelected: boolean; disabled: boolean }>(
  ({ theme, isSelected, disabled }) => css`
    font-size: ${theme.fontSizes.E};
    width: 100%;
    text-align: left;
    margin-bottom: 4px;
    text-transform: inherit;
    opacity: ${isSelected ? (disabled ? 0.5 : 1) : 0.5};
  `
)

export const MobileSmall = styled.small<{ isSelected: boolean }>`
  font-size: ${({ theme }) => theme.fontSizes.E};
  width: 100%;
  opacity: 0.7;
  pointer-events: none;
  padding: 4px 0;
  color: ${({ theme, isSelected }) =>
    isSelected
      ? theme.colors.foreground.secondary.A
      : theme.colors.foreground.secondary.D};
`

export const MobilePrice = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-right: 12px;
  position: relative;
`

export const MobileCurrency = styled(Currency)`
  font-size: ${({ theme }) => theme.fontSizes.H};
  position: absolute;
  top: -6px;
  left: -16px;
  letter-spacing: -6px;
`

export const MobileUnit = styled(Unit)`
  font-size: ${({ theme }) => theme.fontSizes.I};
`
